import { ChevronDown } from "lucide-react";
import { SearchablePopover } from "@racoon/ui";
import { useT, useTypedT, Namespace } from "@racoon/i18n";
import { getEnumName } from "@racoon/lib";
import { ConnectProto } from "@racoon/api/src";

/** A location id can outgrow `Number`, so a caller may key its depots by string. */
type DepotId = string | number;

interface DepotBarProps<TId extends DepotId> {
  depots: any[];
  filteredDepots: any[];
  depotsLoading: boolean;
  selectedDepot: TId | null;
  isDepotPopoverOpen: boolean;
  onDepotPopoverChange: (open: boolean) => void;
  depotSearchQuery: string;
  onDepotSearchQueryChange: (value: string) => void;
  onSelectDepot: (id: TId) => void;
  routeDurationText: string;
  /** When set, an extra "no unloading" option selects this id. */
  noUnloadingId?: TId;
}

export const DepotBar = <TId extends DepotId>({
  depots,
  filteredDepots,
  depotsLoading,
  selectedDepot,
  isDepotPopoverOpen,
  onDepotPopoverChange,
  depotSearchQuery,
  onDepotSearchQueryChange,
  onSelectDepot,
  routeDurationText,
  noUnloadingId,
}: DepotBarProps<TId>) => {
  const t = useTypedT(Namespace.Routes);
  const tAny = useT();
  const allowNoUnloading = noUnloadingId !== undefined;
  const singleDepot = depots.length === 1 && !allowNoUnloading;
  const selectedDepotName =
    allowNoUnloading && selectedDepot === noUnloadingId
      ? t("noUnloading")
      : depots.find((d) => d.id === selectedDepot)?.name;

  const depotLabel = depotsLoading
    ? t("loading")
    : selectedDepot
      ? selectedDepotName || tAny(Namespace.Depot, "selectDepot")
      : tAny(Namespace.Depot, "selectDepot");

  return (
    <div className="flex items-center justify-between gap-4 rounded-b-xl border border-gray-200 bg-white p-4">
      <div className="flex h-8 w-[440px] items-center overflow-hidden rounded-lg border border-gray-200">
        <div className="flex h-full w-[100px] shrink-0 items-center justify-center bg-gray-100 px-4">
          <span className="text-sm font-medium text-text-primary">{t("unloading")}</span>
        </div>
        <div className="flex h-full min-w-0 flex-1 items-center justify-end px-2">
          {singleDepot ? (
            <span className="truncate text-sm text-text-primary">{depotLabel}</span>
          ) : (
            <SearchablePopover
              open={isDepotPopoverOpen}
              onOpenChange={(open) => {
                onDepotPopoverChange(open);
                if (!open) {
                  onDepotSearchQueryChange("");
                }
              }}
              trigger={
                <button className="flex items-center gap-1 focus:outline-none">
                  <span className="truncate text-sm text-text-primary">{depotLabel}</span>
                  <ChevronDown className="h-4 w-4 shrink-0 text-text-secondary" />
                </button>
              }
              searchValue={depotSearchQuery}
              onSearchChange={onDepotSearchQueryChange}
              inputId="assign-route-depot-search-input"
              placeholder={tAny(Namespace.Common, "search")}
            >
              {noUnloadingId !== undefined && !depotSearchQuery.trim() && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onSelectDepot(noUnloadingId);
                    onDepotPopoverChange(false);
                  }}
                  className={[
                    "w-full px-4 py-2 text-left text-sm hover:bg-gray-100",
                    selectedDepot === noUnloadingId ? "bg-blue-50 text-brand-blue" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  {t("noUnloading")}
                </button>
              )}
              {filteredDepots.map((depot) => (
                <button
                  key={depot.id}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onSelectDepot(depot.id);
                    onDepotPopoverChange(false);
                    onDepotSearchQueryChange("");
                  }}
                  className={[
                    "w-full px-4 py-2 text-left flex justify-between text-sm hover:bg-gray-100",
                    depot.id === selectedDepot ? "bg-blue-50 text-brand-blue" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  <span className="">{depot.name}</span>
                  <span className="text-xs text-text-secondary">
                    {tAny(
                      Namespace.Enum,
                      getEnumName(ConnectProto.LocationTypeSchema, depot.type) ??
                        "LOCATION_TYPE_UNDEFINED",
                    )}
                  </span>
                </button>
              ))}
              {filteredDepots.length === 0 && (
                <div className="px-4 py-6 text-center text-sm text-gray-500">
                  {depotSearchQuery.trim()
                    ? tAny(Namespace.Depot, "notFound")
                    : tAny(Namespace.Depot, "noneAvailable")}
                </div>
              )}
            </SearchablePopover>
          )}
        </div>
      </div>

      <span className="text-xs font-medium text-text-primary">{routeDurationText}</span>
    </div>
  );
};
